// @ts-nocheck
import React, { useState, useEffect } from "react";
import { ReactComponent as GroupIcon } from "../../assets/icons/group-icon.svg";
import { ReactComponent as XClose } from "../../assets/icons/x-close.svg";
import { ReactComponent as PowerIcon } from "../../assets/icons/lightning.svg";
import { useDispatch, useSelector } from "react-redux";
import { setModalOpen, closeModalWithResult } from "../../store/modalSlice";
import { z } from "zod";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import PCurveGraph from "../PerformanceCurve/PCurveGraph";
import { editPCurve, postPCurve } from "../../store/pCurveSlice";

import Select from "../ReusableComponents/Select";
const PCurveSchema = z.object({
  name: z.string().trim().min(2).max(50),
  description: z.string().trim(),
  xUnit: z.enum(["m/s"], {
    errorMap: (issue, ctx) => ({ message: "Invalid Unit" }),
  }),
  yUnit: z.enum(["MW"], {
    errorMap: (issue, ctx) => ({ message: "Invalid Unit" }),
  }),
  curveData: z
    .array(z.object({ x: z.number(), y: z.number() }))
    .min(2, { message: "At least two Curve Data points must be provided." }), // Enforce a minimum of 2 items in the array,
});
type PCurveType = z.infer<typeof PCurveSchema>;

interface PerformanceCurveFormProps {
  editFlag?: boolean;
  initialPCurve: any;
  modalType?: 'callback' | 'edit' | 'new';
  onClose?: (result?: any) => void;
}

const PerformanceCurveForm: React.FC<PerformanceCurveFormProps> = ({ 
  editFlag = false, 
  initialPCurve,
  modalType = 'new',
  onClose
}) => {
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    control,
    formState: { errors },
    getValues,
    watch,
  } = useForm<PCurveType>({
    defaultValues: {
      name: initialPCurve?.name || "",
      description: initialPCurve?.description || "",
      xUnit: initialPCurve?.xUnit || "m/s",
      yUnit: initialPCurve?.yUnit || "MW",
      curveData: initialPCurve?.curveData || [{ x: 0, y: 0 }],
    },
    resolver: zodResolver(PCurveSchema),
    mode: "onChange",
  });
  const [curveViewerOpen, setCurveViewerOpen] = useState(false);
  const { fields, append, remove } = useFieldArray({
    control,
    name: "curveData",
  });
  const curveDataWatch = watch("curveData"); //needed to updated curve Data

  const handleCancel = () => {
    if (modalType === 'callback' && onClose) {
      onClose();
    } else if (modalType === 'callback') {
      dispatch(closeModalWithResult({ success: false, action: 'cancel' }));
    } else {
      dispatch(setModalOpen(false));
    }
  };

  const submitPCurve = async (data) => {
    try {
      // Handle the submission based on modalType
      switch (modalType) {
        case 'callback':
          // Just return the data to the caller without any API calls
          if (onClose) {
            onClose({
              success: true,
              windCurve: { id: initialPCurve.id, ...data },
              action: 'onClose'
            });
          } else {
            dispatch(closeModalWithResult({
              success: true,
              windCurve: { id: initialPCurve.id, ...data },
              action: 'onClose'
            }));
          }
          break;
        
        default:
          // Normal behavior - edit or create through Redux
          if (editFlag) {
            await dispatch(editPCurve({ id: initialPCurve.id, ...data }));
          } else {
            await dispatch(postPCurve(data));
          }
          dispatch(setModalOpen(false));
          break;
      }
    } catch (error) {
      console.error('Error saving performance curve:', error);
      
      // Close regardless of error
      if (onClose) {
        onClose({
          success: false,
          error: error
        });
      } else {
        dispatch(closeModalWithResult({
          success: false,
          error: error
        }));
      }
    }
  };

  const setMainForm = async () => {
    setCurveViewerOpen(false);
  };

  if (curveViewerOpen) {
    return (
      <div className="">
        <div
          onClick={() => dispatch(setModalOpen(false))}
          className=" flex flow-row grow items-center border-b border-gray-200 p-5"
        >
          <div className="flex flex-0 w-10 h-10 bg-white rounded-md shadow border border-gray-200 mr-3 items-center justify-center">
            <PowerIcon stroke="#A2BBBF" className="h-6 w-6" />
          </div>
          <div className="flex grow text-gray-900 text-xl font-semibold font-Inter">
            {editFlag
              ? `Edit ${initialPCurve.name} Curve Data`
              : "New Curve Data"}
          </div>
          <div
            onClick={() => dispatch(setModalOpen(false))}
            className="cursor-pointer flex flex-0 w-10 h-10 bg-white items-center justify-center"
          >
            <XClose className="h-6 w-6" />
          </div>
        </div>
        <div className="px-4 py-2 flex">
          <div className="flex-1 border border-gray-200 rounded-md shadow-sm overflow-hidden">
            <div className="overflow-y-auto ">
              <div className="flex bg-gray-50 py-3 sticky top-0">
                <div className="flex-1 text-center font-medium text-gray-700">{getValues("xUnit")}</div>
                <div className="flex-1 text-center font-medium text-gray-700">{getValues("yUnit")}</div>
                <div className="flex-1 text-center font-medium text-gray-700">Actions</div>
              </div>
              <div className="max-h-[300px] overflow-y-auto px-2">
                {fields.map((item, index) => (
                  <div key={item.id} className="flex py-2 border-b border-gray-100 hover:bg-gray-50">
                    <div className="flex-1 px-2">
                      <Controller
                        name={`curveData.${index}.x`}
                        render={({ field }) => {
                          return (
                            <input
                              min="0"
                              type="text"
                              pattern="[0-9]*[.]?[0-9]*"
                              inputMode="decimal"
                              className="border rounded-md p-2 w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
                              onWheel={(e) => e.target.blur()}
                              onKeyDown={(e) => {
                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                  e.preventDefault();
                                }
                              }}
                              {...field}
                              value={field.value}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Allow typing decimal points as part of a number
                                if (value === '' || value === '.') {
                                  field.onChange(value);
                                } else if (/^[0-9]*[.]?[0-9]*$/.test(value)) {
                                  // Only convert to parseFloat when we have a complete number
                                  if (value.endsWith('.')) {
                                    field.onChange(value);
                                  } else {
                                    field.onChange(parseFloat(value) || 0);
                                  }
                                }
                              }}
                            />
                          );
                        }}
                        control={control}
                      />
                    </div>
                    <div className="flex-1 px-2">
                      <Controller
                        name={`curveData.${index}.y`}
                        render={({ field }) => (
                          <input
                            min="0"
                            type="text"
                            pattern="[0-9]*[.]?[0-9]*"
                            inputMode="decimal"
                            className="border rounded-md p-2 w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
                            onWheel={(e) => e.target.blur()}
                            onKeyDown={(e) => {
                              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                e.preventDefault();
                              }
                            }}
                            {...field}
                            value={field.value}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Allow typing decimal points as part of a number
                              if (value === '' || value === '.') {
                                field.onChange(value);
                              } else if (/^[0-9]*[.]?[0-9]*$/.test(value)) {
                                // Only convert to parseFloat when we have a complete number
                                if (value.endsWith('.')) {
                                  field.onChange(value);
                                } else {
                                  field.onChange(parseFloat(value) || 0);
                                }
                              }
                            }}
                          />
                        )}
                        control={control}
                      />
                    </div>
                    <div className="flex-1 flex justify-center items-center">
                      <button
                        className="eai-btn-red px-3 py-1 text-sm"
                        type="button"
                        onClick={() => remove(index)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 bg-gray-50 border-t border-gray-200">
                <button
                  className="eai-btn-grn px-4 py-2 w-full flex items-center justify-center"
                  type="button"
                  onClick={() =>{
                    if(curveDataWatch.length > 0) {
                      append({
                        x: curveDataWatch[curveDataWatch.length - 1].x,
                        y: curveDataWatch[curveDataWatch.length - 1].y,
                      })
                    } else {
                      append({
                        x: 0,
                        y: 0,
                      })
                    }
                  }}
                >
                  + Add Point
                </button>
              </div>
            </div>
          </div>
          <div className="flex-1 ml-4">
            <PCurveGraph
              data={curveDataWatch}
              xAxisLabel={getValues("xUnit")}
              yAxisLabel={getValues("yUnit")}
            />
          </div>
        </div>
        <div className="flex flex-row grow space-x-3 p-5">
          <button className="flex-grow eai-btn my-1" type="submit">
            <div
              className="mx-3"
              onClick={() => setMainForm()} //Later save curve and if cancel, don't add other features
            >
              Cancel
            </div>
          </button>
          <button
            className="flex-grow eai-btn-grn my-1"
            onClick={() => setMainForm()}
          >
            Accept Curve
          </button>
        </div>
      </div>
    );
  }

  return (
    <form id="my-form" onSubmit={handleSubmit(submitPCurve)} className="">
      <div
        className=" flex flow-row grow items-center border-b border-gray-200 p-5"
      >
        <div className="flex flex-0 w-10 h-10 bg-white rounded-md shadow border border-gray-200 mr-3 items-center justify-center">
          <PowerIcon stroke="#A2BBBF" className="h-6 w-6" />
        </div>
        <div className="flex grow text-gray-900 text-xl font-semibold font-Inter">
          {editFlag
            ? `Edit Energy Model: ${initialPCurve.name}`
            : "New Wind Energy Model"}
        </div>
        <div
          onClick={() => dispatch(setModalOpen(false))}
          className="cursor-pointer flex flex-0 w-10 h-10 bg-white items-center justify-center"
        >
          <XClose className="h-6 w-6" />
        </div>
      </div>

      <div className="h-1 flex grow">
        {/* <div className="h-1 w-full bg-eai-primary-500"></div> */}
      </div>
      <div className="flex flex-col grow p-5">
        <div className="flex gap-8 justify-between">
          <div className="flex-1">
            <div className="flex justify-between">
              <div
                name="name"
                className="text-gray-500 text-sm mb-2 font-normal font-['Inter'] leading-[18px]"
              >
                Curve Name
              </div>
              {errors.name ? (
                <span className="text-red-500 text-sm mb-2 font-normal font-['Inter'] leading-[18px]">
                  {errors.name.message}
                </span>
              ) : (
                <span></span>
              )}
            </div>
            <div className="flex">
              <input
                name="name"
                {...register("name")}
                className="flex grow border p-3 mb-5 text-md rounded-md"
              />
            </div>
          </div>
          <div className="flex-1">
            <div className="flex justify-between">
              <div className="text-gray-500 text-sm mb-2 font-normal font-['Inter'] leading-[18px]">
                Type
              </div>
              {errors?.type ? (
                <span className="text-red-500 text-sm mb-2 font-normal font-['Inter'] leading-[18px]">
                  {errors.type.message}
                </span>
              ) : (
                <span></span>
              )}
            </div>
            <div className="flex grow">
              <div
                disabled
                className="flex bg-slate-400 grow border p-3 mb-5 text-md rounded-md"
              >
                Wind
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-8 justify-between">
          <div className="flex-1">
            <div className="flex justify-between">
              <div className="text-gray-500 text-sm mb-2 font-normal font-['Inter'] leading-[18px]">
                X axis Unit
              </div>
              {errors.xUnit ? (
                <span className="text-red-500 text-sm mb-2 font-normal font-['Inter'] leading-[18px]">
                  {errors.xUnit.message}
                </span>
              ) : (
                <span></span>
              )}
            </div>
            <div className="flex grow">
              <Select
                name="xaxisunit"
                {...register("xUnit")}
                containerClassName="grow mb-5"
              >
                <option value="">--</option>
                <option value="m/s">m/s</option>
                {/* <option value="precip">Precipitation</option> <option value="precip">Wind</option> */}
              </Select>
            </div>
          </div>
          <div className="flex-1">
            <div className="flex justify-between">
              <div className="text-gray-500 text-sm mb-2 font-normal font-['Inter'] leading-[18px]">
                Y axis Unit
              </div>
              {errors.yUnit ? (
                <span className="text-red-500 text-sm mb-2 font-normal font-['Inter'] leading-[18px]">
                  {errors.yUnit.message}
                </span>
              ) : (
                <span></span>
              )}
            </div>
            <div className="flex grow">
              <Select
                name="yaxisunit"
                {...register("yUnit")}
                containerClassName="grow mb-5"
              >
                <option value="">--</option>
                <option value="MW">MW</option>

                {/* <option value="precip">Precipitation</option> <option value="precip">Wind</option> */}
              </Select>
            </div>
          </div>
        </div>
        <div className="text-gray-500 text-sm mb-2 font-normal font-['Inter'] leading-[18px]">
          Curve Description
        </div>
        <div className="flex grow">
          <textarea
            name="description"
            {...register("description")}
            className="flex grow border p-3 mb-2 text-md rounded-md"
          />
        </div>
        <div className="flex justify-between">
          <button
            onClick={() => setCurveViewerOpen(true)}
            className={curveDataWatch.length > 1 ? "eai-btn-grn" : "eai-btn" }
          >
            Curve Data
          </button>

          {errors.curveData ? (
            <span className="text-red-500 text-sm mb-2 font-normal font-['Inter'] leading-[18px]">
              {errors.curveData?.message}
            </span>
          ) : (
            <span></span>
          )}
        </div>
      </div>
      <div className="flex flex-row grow space-x-3 p-5">
      <button className="flex-grow eai-btn my-1" type="button">
          <div className="mx-3" onClick={handleCancel}>
            Cancel
          </div>
        </button>
        <button
          className="flex-grow eai-btn-grn my-1"
          type="submit"
          form="my-form"
        >
          <div className="mx-3">{editFlag ? "Update" : "Submit"}</div>
        </button>

      </div>
    </form>
  );
};

export default PerformanceCurveForm;
